import { absoluteDate, stripMarkdown } from './format';
import { getRiskStyle } from './risk';
import { formatSourceLabel, isVerifiedSource } from '../constants/trustedSources';
import { groupDuplicateStories } from './storyGrouping';

function escapeCsv(value) {
  const str = String(value ?? '').replace(/"/g, '""');
  return /[",\n\r]/.test(str) ? `"${str}"` : str;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function riskColor(severity) {
  const level = getRiskStyle(severity).label;
  if (level.includes('HIGH')) return '#dc2626';
  if (level.includes('MEDIUM')) return '#d97706';
  if (level.includes('LOW')) return '#16a34a';
  return '#64748b';
}

function buildExportRows(records, { grouped = true } = {}) {
  const items = grouped ? groupDuplicateStories(records) : records.map(r => ({
    primary: r,
    related: [],
    sourceCount: 1,
  }));

  return items.map(({ primary, related, sourceCount }) => {
    const sources = [primary, ...related]
      .map(r => formatSourceLabel(r.source_domain))
      .filter(Boolean);
    const uniqueSources = [...new Set(sources)];
    const style = getRiskStyle(primary.severity);
    const summary = stripMarkdown(primary.summary || primary.snippet || primary.content || '');

    return {
      company: primary.company || '',
      date: absoluteDate(primary.published_at),
      severity: style.label.replace(' RISK', ''),
      severityRaw: primary.severity,
      title: primary.title || 'Untitled',
      source: uniqueSources.join('; ') || primary.source_domain || '',
      sourceCount: sourceCount > 1 ? sourceCount : uniqueSources.length,
      verified: [primary, ...related].some(r => isVerifiedSource(r.source_domain)) ? 'Yes' : 'No',
      categories: (primary.categories || []).join(', '),
      url: primary.url || '',
      summary: summary.slice(0, 500),
    };
  });
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function reportFilename(ext) {
  const stamp = new Date().toISOString().slice(0, 10);
  return `corp-sentinel-alerts-${stamp}.${ext}`;
}

function buildPrintableReportHtml(rows, meta = {}) {
  const generated = new Date().toLocaleString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const tableRows = rows.map(r => `
    <tr>
      <td>${escapeHtml(r.company)}</td>
      <td>${escapeHtml(r.date)}</td>
      <td><span class="risk" style="color:${riskColor(r.severityRaw)}">${escapeHtml(r.severity)}</span></td>
      <td>${escapeHtml(r.title)}</td>
      <td>${escapeHtml(r.sourceCount > 1 ? `${r.sourceCount} sources` : r.source)}</td>
      <td>${escapeHtml(r.categories || '—')}</td>
    </tr>
  `).join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Corp Sentinel Intelligence Report</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: "Segoe UI", Arial, sans-serif; color: #1e293b; margin: 0; padding: 24px; font-size: 11px; }
    header { background: #0f172a; color: #fff; padding: 16px 20px; border-radius: 8px; margin-bottom: 20px; }
    header h1 { margin: 0 0 4px; font-size: 18px; }
    header p { margin: 0; opacity: 0.85; font-size: 11px; }
    .meta { display: flex; justify-content: space-between; gap: 16px; margin-bottom: 16px; font-size: 10px; color: #64748b; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #2563eb; color: #fff; text-align: left; padding: 8px 10px; font-size: 10px; }
    td { border-bottom: 1px solid #e2e8f0; padding: 8px 10px; vertical-align: top; line-height: 1.4; }
    tr:nth-child(even) td { background: #f8fafc; }
    .risk { font-weight: 700; }
    footer { margin-top: 20px; text-align: center; font-size: 9px; color: #94a3b8; }
    @media print {
      body { padding: 12px; }
      header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      th { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <header>
    <h1>Corp Sentinel — Intelligence Report</h1>
    <p>${escapeHtml(meta.subtitle || 'Corporate risk & regulatory alerts')}</p>
  </header>
  <div class="meta">
    <span>${rows.length} stor${rows.length === 1 ? 'y' : 'ies'} · Confidential — for internal / client use</span>
    <span>Generated ${escapeHtml(generated)}${meta.filterLabel ? ` · Filters: ${escapeHtml(meta.filterLabel)}` : ''}</span>
  </div>
  <table>
    <thead>
      <tr>
        <th>Company</th>
        <th>Date</th>
        <th>Risk</th>
        <th>Headline</th>
        <th>Source(s)</th>
        <th>Category</th>
      </tr>
    </thead>
    <tbody>${tableRows}</tbody>
  </table>
  <footer>Corp Sentinel · Altius Soft</footer>
  <script>window.onload = function() { window.print(); };</script>
</body>
</html>`;
}

export function exportAlertsToExcel(records, meta = {}) {
  const rows = buildExportRows(records, { grouped: meta.grouped !== false });
  const headers = ['Company', 'Date', 'Severity', 'Headline', 'Sources', 'Source Count', 'Verified', 'Categories', 'URL', 'Summary'];

  const lines = [
    headers.map(escapeCsv).join(','),
    ...rows.map(row => [
      row.company,
      row.date,
      row.severity,
      row.title,
      row.source,
      row.sourceCount,
      row.verified,
      row.categories,
      row.url,
      row.summary,
    ].map(escapeCsv).join(',')),
  ];

  const bom = '\uFEFF';
  const blob = new Blob([bom + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, reportFilename('csv'));
}

export function exportAlertsToPdf(records, meta = {}) {
  const rows = buildExportRows(records, { grouped: meta.grouped !== false });
  const html = buildPrintableReportHtml(rows, meta);
  const printWin = window.open('', '_blank', 'noopener,noreferrer');

  if (!printWin) {
    downloadBlob(new Blob([html], { type: 'text/html;charset=utf-8' }), reportFilename('html'));
    return;
  }

  printWin.document.open();
  printWin.document.write(html);
  printWin.document.close();
}